import { type FC, useCallback, useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Doc } from "sharedb/lib/client";

import type {
  OEmbedObjectType,
  OEmbedPhotoType,
  OEmbedRichType,
  OEmbedVideoType,
} from "../../../../shared/types/oembed";
import type { EmbedFieldType, PathType, WorksheetType } from "../../../../shared/types/worksheet";
import { VideoState } from "../../../../shared/types/worksheet";

import {
  VideoChangeMessage,
  WindowMessages,
  addProjectorMessageListener,
  removeProjectorMessageListener,
  sendMessageToProjectorView,
} from "components/materials/presentation/projector/projector-messaging";
import { t } from "i18n/i18n";
import { DocViewContext } from "providers/DocViewProvider";
import { clearVideoStateOnDoc, getDefaultVideoState, setVideoStateOnDoc } from "utils/asset";
import { useAv } from "utils/av";
import { rollbarAndLogError } from "utils/logger";
import { fetchOEmbed } from "utils/oembed";
import { usePresentation } from "utils/presentation";

interface EmbedProps {
  doc: Doc<WorksheetType>;
  path: PathType;
}

type EmbedInfoType = {
  id: string;
  userIsProjecting: boolean;
};

enum PlayerState {
  UNSTARTED = -1,
  ENDED = 0,
  PLAYING = 1,
  PAUSED = 2,
  BUFFERING = 3,
  VIDEO_CUED = 5,
}

type YouTubeMessage = {
  event: "infoDelivery";
  info: {
    playerState: PlayerState;
    currentTime: number;
    playbackRate: number;
    videoData?: {
      video_id: string;
    };
  };
};

export const EmbedField: FC<EmbedProps & EmbedFieldType> = ({
  url,
  o,
  id,
  doc,
  path,
  videoState,
}) => {
  const { canAuthorPage, isProjecting, isFeaturing } = useContext(DocViewContext);
  const [oEmbed, setOEmbed] = useState<OEmbedObjectType>(o as OEmbedObjectType);
  const { isPresenting, hasProjectorView, isFollowing } = usePresentation();
  const { isInVirtualClass } = useAv();
  const currentVideoState = videoState || getDefaultVideoState();

  const playbackRateRef = useRef(1);
  // This field serves dual purposes as it's used both by the instructor view and the projector view
  // Each of them set it at a different point as well.
  const videoPlayingRef = useRef(false);
  const didSaveVideoState = useRef(false);
  const isFullscreenRef = useRef(false);
  const lastCurrentTimeRef = useRef<number | null>(null);
  const [showFullscreenPortal, setShowFullscreenPortal] = useState(false);
  const originalParentRef = useRef<HTMLElement | null>(null);
  const shouldSyncWithProjectorView = Boolean(isPresenting && hasProjectorView);
  const shouldSaveVideoStateToDoc = isPresenting && isInVirtualClass;
  const shouldRespondToVideoState = Boolean(!isPresenting && isFollowing && isInVirtualClass);
  const shouldSyncVideoChanges = shouldSyncWithProjectorView || shouldSaveVideoStateToDoc;
  const embedId = isFeaturing ? `featuring-embed-${id}` : `embed-${id}`;

  useEffect(() => {
    const checkEmbed = async () => {
      const { embed } = await fetchOEmbed(url);
      setOEmbed((embed as OEmbedObjectType) || null);
    };

    if (url) {
      checkEmbed();
    }
  }, [url]);

  const maybeSaveVideoState = useCallback(
    (newVideoState: Partial<VideoState>) => {
      if (shouldSaveVideoStateToDoc) {
        setVideoStateOnDoc(doc, path, currentVideoState, newVideoState);
        didSaveVideoState.current = true;
      }
    },
    [shouldSaveVideoStateToDoc, doc, path, currentVideoState],
  );

  const handleYouTubeMessage = useCallback(
    (event: MessageEvent<string>) => {
      if (event.origin !== "https://www.youtube.com" || !shouldSyncVideoChanges) {
        return;
      }

      const data = JSON.parse(event.data) as YouTubeMessage;

      const playerState = data?.info?.playerState;
      const currentTimeInSeconds = data?.info?.currentTime;
      const playbackRate = data?.info?.playbackRate;
      const videoId = data?.info?.videoData?.video_id;

      if (currentTimeInSeconds) {
        lastCurrentTimeRef.current = currentTimeInSeconds;
      }

      if (
        (videoPlayingRef.current || (videoId && url.includes(videoId))) &&
        [PlayerState.PLAYING, PlayerState.ENDED, PlayerState.PAUSED].includes(playerState)
      ) {
        videoPlayingRef.current = playerState === PlayerState.PLAYING;

        const newVideoState: Partial<VideoState> = {
          playing: videoPlayingRef.current,
        };

        if (currentTimeInSeconds) {
          newVideoState.currentTimeInSeconds = currentTimeInSeconds;
        }

        if (shouldSyncWithProjectorView) {
          const message: VideoChangeMessage = {
            type: WindowMessages.VIDEO_CHANGE,
            id,
            ...newVideoState,
          };

          sendMessageToProjectorView(message);
        }

        maybeSaveVideoState(newVideoState);
      } else if (
        videoPlayingRef.current &&
        playbackRate &&
        playbackRate !== playbackRateRef.current
      ) {
        playbackRateRef.current = playbackRate;

        if (shouldSyncWithProjectorView) {
          const message: VideoChangeMessage = {
            type: WindowMessages.VIDEO_CHANGE,
            id,
            playbackRate,
          };

          sendMessageToProjectorView(message);
        }

        maybeSaveVideoState({ playbackRate });
      }
    },
    [id, url, shouldSyncWithProjectorView, maybeSaveVideoState, shouldSyncVideoChanges],
  );

  // If we're presenting we store our video changes in the doc or pass them to the project via messaging.
  // This, however, is an embedded field, so we don't actually have access to the video being changed.
  // The only way to know that changes were made is to listen to the youtube events, but first we
  // need to turn them on.
  useEffect(() => {
    if (shouldSyncVideoChanges && oEmbed) {
      const embedIframe: HTMLIFrameElement = document.getElementById(embedId) as HTMLIFrameElement;

      // Tell youtube we want to listen to status changes
      embedIframe.onload = () => {
        embedIframe.contentWindow?.postMessage(JSON.stringify({ event: "listening" }), "*");
      };

      // Listen for messages from the iframe
      window.addEventListener("message", handleYouTubeMessage);

      return () => {
        window.removeEventListener("message", handleYouTubeMessage);
      };
    }
  }, [
    // eslint-disable-next-line @minervaproject/no-object-deps/no-object-deps
    oEmbed,
    embedId,
    shouldSyncVideoChanges,
    // eslint-disable-next-line @minervaproject/no-object-deps/no-object-deps
    handleYouTubeMessage,
  ]);

  // Listen for fullscreen changes to notify projector view
  useEffect(() => {
    if (shouldSyncWithProjectorView) {
      const handleFullscreenChange = () => {
        const embedIframe = document.getElementById(embedId) as HTMLIFrameElement;

        if (embedIframe) {
          const isCurrentlyFullscreen = Boolean(document.fullscreenElement === embedIframe);

          if (isCurrentlyFullscreen !== isFullscreenRef.current) {
            isFullscreenRef.current = isCurrentlyFullscreen;
            const fullscreenMessage: VideoChangeMessage = {
              type: WindowMessages.VIDEO_CHANGE,
              id,
              isFullscreen: isCurrentlyFullscreen,
            };

            sendMessageToProjectorView(fullscreenMessage);

            if (videoPlayingRef.current) {
              setTimeout(() => {
                // When going to fullscreen the video tends to stop playing, so we try to restart it
                const startMessage: VideoChangeMessage = {
                  type: WindowMessages.VIDEO_CHANGE,
                  id,
                  playing: true,
                  currentTimeInSeconds: lastCurrentTimeRef.current || 0,
                  playbackRate: playbackRateRef.current || 1,
                };

                sendMessageToProjectorView(startMessage);
              }, 1000);
            }
          }
        }
      };

      document.addEventListener("fullscreenchange", handleFullscreenChange);
      document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
      document.addEventListener("mozfullscreenchange", handleFullscreenChange);
      document.addEventListener("MSFullscreenChange", handleFullscreenChange);

      return () => {
        document.removeEventListener("fullscreenchange", handleFullscreenChange);
        document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
        document.removeEventListener("mozfullscreenchange", handleFullscreenChange);
        document.removeEventListener("MSFullscreenChange", handleFullscreenChange);
      };
    }
  }, [shouldSyncWithProjectorView, embedId, id]);

  const syncWithVideoChange = useCallback(
    (state?: VideoState) => {
      const embedIframe: HTMLIFrameElement = document.getElementById(embedId) as HTMLIFrameElement;

      if (state?.currentTimeInSeconds) {
        embedIframe.contentWindow?.postMessage(
          JSON.stringify({
            event: "command",
            func: "seekTo",
            args: [state.currentTimeInSeconds],
          }),
          "*",
        );
      }

      if (state?.playbackRate) {
        embedIframe.contentWindow?.postMessage(
          JSON.stringify({
            event: "command",
            func: "setPlaybackRate",
            args: [state.playbackRate],
          }),
          "*",
        );
      }

      if (state?.playing && !videoPlayingRef.current) {
        embedIframe.contentWindow?.postMessage(
          JSON.stringify({ event: "command", func: "playVideo", args: [] }),
          "*",
        );

        videoPlayingRef.current = true;
      } else if (state?.playing === false && videoPlayingRef.current) {
        embedIframe.contentWindow?.postMessage(
          JSON.stringify({ event: "command", func: "pauseVideo", args: [] }),
          "*",
        );

        videoPlayingRef.current = false;
      }
    },
    [embedId],
  );

  // If we're projecting we need to listen to change messages.
  // TODO: We might just listen for doc changes like presentation followers
  // rather than keep this messaging between tabs.  Need to make sure there's not too
  // much lag between updating the doc and reacting to the change in projector view
  useEffect(() => {
    const handleFullscreenChange = (message: MessageEvent<VideoChangeMessage>) => {
      // Handle fullscreen state changes
      if (message.data.isFullscreen) {
        // Show fullscreen portal and move iframe
        setShowFullscreenPortal(true);
        document.body.style.overflow = "hidden";

        // Move the original iframe to the fullscreen container
        setTimeout(() => {
          const originalIframe = document.getElementById(embedId) as HTMLIFrameElement;
          const fullscreenContainer = document.getElementById(`${embedId}-fullscreen-container`);

          if (originalIframe && fullscreenContainer) {
            // Store original parent for restoration
            originalParentRef.current = originalIframe.parentElement;

            // Move iframe to fullscreen container
            fullscreenContainer.appendChild(originalIframe);
            // Full screen stops playing the video :(
            videoPlayingRef.current = false;

            // Apply fullscreen styling to iframe
            originalIframe.style.width = "100vw";
            originalIframe.style.height = "100vh";
            originalIframe.style.maxWidth = "none";
            originalIframe.style.aspectRatio = "unset";
          }
        }, 0);
      } else {
        // Get the iframe reference and move it back immediately
        const originalIframe = document.getElementById(embedId) as HTMLIFrameElement;

        if (originalIframe && originalParentRef.current) {
          // Move iframe back to original parent
          originalParentRef.current.appendChild(originalIframe);

          // Restore original styling
          originalIframe.style.width = "";
          originalIframe.style.height = "";
          originalIframe.style.maxWidth = "";
          originalIframe.style.aspectRatio = "";

          // Clear the ref
          originalParentRef.current = null;
        }

        // Hide fullscreen portal after iframe has been moved
        setShowFullscreenPortal(false);
        document.body.style.overflow = "";
      }
    };

    const handleYoutubeChangeMessage = (message: MessageEvent<VideoChangeMessage>) => {
      if (
        message.data.type === WindowMessages.VIDEO_CHANGE &&
        isProjecting &&
        message.data.id === id
      ) {
        if (message.data.isFullscreen !== undefined) {
          handleFullscreenChange(message);
        }

        // Handle video state changes
        syncWithVideoChange(message.data as VideoState);
      }
    };

    if (isProjecting) {
      addProjectorMessageListener(handleYoutubeChangeMessage);
    }

    return () => {
      removeProjectorMessageListener(handleYoutubeChangeMessage);
    };

    // eslint-disable-next-line @minervaproject/no-object-deps/no-object-deps
  }, [isProjecting, id, syncWithVideoChange, embedId]);

  // Cleanup effect to restore iframe if component unmounts while in fullscreen
  useEffect(() => {
    return () => {
      // If component unmounts while in fullscreen, restore iframe
      if (showFullscreenPortal && originalParentRef.current) {
        const originalIframe = document.getElementById(embedId) as HTMLIFrameElement;
        if (originalIframe) {
          originalParentRef.current.appendChild(originalIframe);
          originalIframe.style.width = "";
          originalIframe.style.height = "";
          originalIframe.style.maxWidth = "";
          originalIframe.style.aspectRatio = "";
        }
      }
    };
  }, [showFullscreenPortal, embedId]);

  // If we're following a presentation we should react to video state changes
  useEffect(() => {
    if (shouldRespondToVideoState) {
      syncWithVideoChange(videoState);
    }
    // eslint-disable-next-line @minervaproject/no-object-deps/no-object-deps
  }, [shouldRespondToVideoState, videoState, syncWithVideoChange]);

  // Clear video state when we stop presenting or unmount
  useEffect(() => {
    if (!isPresenting && didSaveVideoState.current) {
      clearVideoStateOnDoc(doc, path);
    }

    return () => {
      if (didSaveVideoState.current) {
        clearVideoStateOnDoc(doc, path);
      }
    };
    // eslint-disable-next-line @minervaproject/no-object-deps/no-object-deps
  }, [isPresenting, doc, path]);

  const errorKey = url && canAuthorPage ? "url_no_longer_valid" : "url_missing";

  return (
    <>
      {oEmbed ? (
        <OEmbedIframe id={embedId} userIsProjecting={shouldSyncWithProjectorView} {...oEmbed} />
      ) : (
        <div className="w-full rounded-lg bg-red-tint-90 p-4">{t(`fields.embed.${errorKey}`)}</div>
      )}

      {/* Fullscreen portal container - moves the original iframe here when fullscreen */}
      {showFullscreenPortal &&
        isProjecting &&
        createPortal(
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              backgroundColor: "black",
              zIndex: 9999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {/* This div will contain the original iframe when moved here */}
            <div
              aria-modal="true"
              id={`${embedId}-fullscreen-container`}
              role="dialog"
              style={{
                width: "100vw",
                height: "100vh",
                maxWidth: "none",
                aspectRatio: "unset",
              }}
            />
          </div>,
          document.body,
        )}
    </>
  );
};

const OEmbedVideoIframe: FC<OEmbedVideoType & EmbedInfoType> = ({
  id,
  userIsProjecting,
  ...oEmbed
}) => {
  // Get iframe src from html
  const parser = new DOMParser();
  const htmlDoc = parser.parseFromString(oEmbed.html, "text/html");
  const htmlSrc = htmlDoc.querySelector("iframe")?.src;

  // Simple muting for projector mode
  useEffect(() => {
    if (userIsProjecting) {
      const iframe = document.getElementById(id) as HTMLIFrameElement;
      if (iframe) {
        const handleIframeLoad = () => {
          // Tell YouTube we want to listen to state changes
          const listenCommand = JSON.stringify({ event: "listening" });
          iframe.contentWindow?.postMessage(listenCommand, "*");
        };

        const muteVideo = () => {
          // Try to mute the video when it starts playing
          // YouTube API
          const muteCommand = JSON.stringify({ event: "command", func: "mute", args: [] });
          iframe.contentWindow?.postMessage(muteCommand, "*");

          // Also try setting volume to 0
          const volumeCommand = JSON.stringify({ event: "command", func: "setVolume", args: [0] });
          iframe.contentWindow?.postMessage(volumeCommand, "*");

          // Generic mute attempt for other platforms
          const genericCommand = JSON.stringify({ type: "mute", value: true });
          iframe.contentWindow?.postMessage(genericCommand, "*");
        };

        // Add a message listener to mute on play
        const handleMessage = (event: MessageEvent) => {
          if (event.origin === "https://www.youtube.com") {
            try {
              const data = JSON.parse(event.data as string) as YouTubeMessage;

              // Check if video is playing (state 1) and mute it
              if (data.event === "infoDelivery" && data.info?.playerState === PlayerState.PLAYING) {
                muteVideo();
              }
            } catch {
              // Ignore non-JSON messages
            }
          }
        };

        window.addEventListener("message", handleMessage);

        if (iframe.contentDocument?.readyState === "complete") {
          handleIframeLoad();
        } else {
          iframe.addEventListener("load", handleIframeLoad);
        }

        return () => {
          iframe.removeEventListener("load", handleIframeLoad);
          window.removeEventListener("message", handleMessage);
        };
      }
    }
  }, [userIsProjecting, id]);

  if (!htmlSrc) {
    rollbarAndLogError(`[OEmbedVideoIframe] No iframe src found for video embed`, {
      title: oEmbed.title,
      html: oEmbed.html.slice(0, 100),
      id,
      userIsProjecting,
    });
    return <div>{t("fields.embed.unsupported_type", { type: oEmbed.type })}</div>;
  }

  const paramSeparator = htmlSrc.includes("?") ? "&" : "?";
  const mirrorParams = userIsProjecting ? `&mute=1` : "";

  const src = `${htmlSrc}${paramSeparator}enablejsapi=1${mirrorParams}`;

  const aspectRatio = oEmbed.width / oEmbed.height;

  return (
    <iframe
      allow="autoplay; fullscreen"
      className="w-full max-w-[--page-default-max-width]"
      frameBorder="0"
      id={id}
      referrerPolicy="strict-origin-when-cross-origin"
      src={src}
      style={{ aspectRatio }}
      title={`Embedded video: ${oEmbed.title}`}
      allowFullScreen
    />
  );
};

const OEmbedImg: FC<OEmbedPhotoType & EmbedInfoType> = ({ id, ...oEmbed }) => {
  const aspectRatio = oEmbed.width / oEmbed.height;

  return (
    <img
      alt={oEmbed.title}
      className="w-full max-w-[--page-default-max-width]"
      id={id}
      src={oEmbed.url}
      style={{ aspectRatio }}
    />
  );
};

const OEmbedRichIframe: FC<OEmbedRichType & EmbedInfoType> = ({ id, ...oEmbed }) => {
  // Get iframe src from html
  const parser = new DOMParser();
  const htmlDoc = parser.parseFromString(oEmbed.html, "text/html");
  const src = htmlDoc.querySelector("iframe")?.src;

  const aspectRatio = oEmbed.width / oEmbed.height;

  return (
    <iframe
      className="w-full max-w-[--page-default-max-width]"
      frameBorder="0"
      id={id}
      referrerPolicy="strict-origin-when-cross-origin"
      src={src}
      style={{ aspectRatio }}
      title={oEmbed.title}
    />
  );
};

const oEmbedTypeMap: Record<OEmbedObjectType["type"], React.FC<OEmbedObjectType>> = {
  photo: OEmbedImg,
  rich: OEmbedRichIframe,
  video: OEmbedVideoIframe,
};

const OEmbedIframe: FC<OEmbedObjectType & EmbedInfoType> = (props) => {
  const OEmbedComponent = oEmbedTypeMap[props.type];

  if (OEmbedComponent) {
    return <OEmbedComponent {...props} />;
  }

  return <div>{t("fields.embed.unsupported_type", { type: props.type })}</div>;
};
